// @ts-nocheck
/**
 * Minimalist template - clean, simple design with minimal styling
 *   Updated to "gold standard" by:
 *     - Escaping user inputs
 *     - Using data-element for personal projects, achievements, etc.
 *     - Preserving bold with class="preserve-bold"
 *     - Keeping consistent naming so Cloud Function can parse similarly
 */

// Helper to escape HTML special characters
function escapeHtml(str) {
  if (!str || typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Helper to format dates consistently (Month YYYY)
function formatDate(dateString) {
  if (!dateString) return "";
  try {
    var date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return as-is if invalid
    }
    var options = { month: "long", year: "numeric" };
    return date.toLocaleDateString(undefined, options);
  } catch (e) {
    return dateString; // Fallback
  }
}

export const minimalistTemplateHtml = function (resumeData) {
  if (!resumeData) return "";

  var personalInformation = resumeData.personalInformation,
    education = resumeData.education,
    workExperience = resumeData.workExperience,
    additionalQualifications = resumeData.additionalQualifications,
    targetJobInformation = resumeData.targetJobInformation,
    skills = resumeData.skills,
    references = resumeData.references;

  // Start container with data-template
  var html =
    '\n<div data-template="minimalist" class="resume-template" style="font-family: \'Helvetica\', \'Arial\', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333; line-height: 1.5;">\n';

  // -------------------
  // HEADER / PERSONAL INFO
  // -------------------
  if (personalInformation) {
    // Name
    var firstName = escapeHtml(personalInformation.firstName || "");
    var lastName = escapeHtml(personalInformation.lastName || "");
    html +=
      '<h1 data-section="personal-info" data-element="name" style="text-align: center; font-size: 28px; font-weight: 300; margin-bottom: 10px; letter-spacing: 1px;">' +
      firstName +
      " " +
      lastName +
      "</h1>\n\n";

    // Contact info container
    html +=
      '<div data-section="contact-info" style="text-align: center; margin-bottom: 30px; font-size: 14px;">\n';

    var contactElements = [];

    // Email
    if (personalInformation.email) {
      contactElements.push(
        '<span data-element="email">' +
          escapeHtml(personalInformation.email) +
          "</span>"
      );
    }

    // Phone
    if (personalInformation.phone) {
      contactElements.push(
        '<span data-element="phone">' +
          escapeHtml(personalInformation.phone) +
          "</span>"
      );
    }

    // Address
    if (personalInformation.address) {
      contactElements.push(
        '<span data-element="address">' +
          escapeHtml(personalInformation.address) +
          "</span>"
      );
    }

    // Social links
    if (personalInformation.linkedinUrl) {
      var cleanUrl = personalInformation.linkedinUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      contactElements.push(
        '<span data-element="linkedin">' + escapeHtml(cleanUrl) + "</span>"
      );
    }
    if (personalInformation.githubUrl) {
      var cleanUrl2 = personalInformation.githubUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      contactElements.push(
        '<span data-element="github">' + escapeHtml(cleanUrl2) + "</span>"
      );
    }
    if (personalInformation.portfolioUrl) {
      var cleanUrl3 = personalInformation.portfolioUrl
        .replace(/https?:\/\/(www\.)?/, "")
        .replace(/\/$/, "");
      contactElements.push(
        '<span data-element="portfolio">' + escapeHtml(cleanUrl3) + "</span>"
      );
    }

    // Join contact elements
    if (contactElements.length > 0) {
      html +=
        '<p data-element="contact-details">' +
        contactElements.join(" | ") +
        "</p>\n";
    }

    html += "</div>\n";
  }

  // Thin divider
  html +=
    '<hr style="border: none; height: 1px; background-color: #ddd; margin: 20px 0;">\n';

  // -------------------
  // PROFESSIONAL SUMMARY
  // -------------------
  var userSummary =
    (personalInformation && personalInformation.professionalSummary) ||
    (targetJobInformation && targetJobInformation.professionalSummary) ||
    "";
  if (userSummary) {
    html += '<div data-section="summary" style="margin-bottom: 30px;">\n';
    html +=
      '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Professional Summary</h2>\n';
    html +=
      '<p data-element="summary-content" style="margin: 0; font-size: 15px;">' +
      escapeHtml(userSummary) +
      "</p>\n";
    html += "</div>\n";
  }

  // -----
  // SKILLS
  // -----
  if (skills && Array.isArray(skills) && skills.length > 0) {
    html += '<div data-section="skills" style="margin-bottom: 30px;">\n';
    html +=
      '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Skills</h2>\n';

    var isSkillsCategorized = false;
    var categorizedSkills = {};

    // Detect categories
    skills.forEach(function (skill) {
      if (skill && typeof skill === "object" && skill.category && skill.name) {
        isSkillsCategorized = true;
        if (!categorizedSkills[skill.category]) {
          categorizedSkills[skill.category] = [];
        }
        categorizedSkills[skill.category].push(escapeHtml(skill.name));
      }
    });

    if (isSkillsCategorized) {
      // Display skills by category
      Object.keys(categorizedSkills).forEach(function (category) {
        var joined = categorizedSkills[category].join(", ");
        html +=
          '<p data-element="skill-category" style="margin: 5px 0;"><strong class="preserve-bold">' +
          escapeHtml(category) +
          ":</strong> " +
          joined +
          "</p>\n";
      });
    } else {
      // Just comma-separated
      var skillStrings = skills.map(function (s) {
        if (typeof s === "string") {
          return escapeHtml(s);
        } else if (s && typeof s === "object" && s.name) {
          return escapeHtml(s.name);
        }
        return "";
      });
      var skillList = skillStrings.join(", ");
      html +=
        '<p data-element="skills-list" style="margin: 0; font-size: 15px;">' +
        skillList +
        "</p>\n";
    }
    html += "</div>\n";
  }

  // ----------------
  // WORK EXPERIENCE
  // ----------------
  if (
    workExperience &&
    Array.isArray(workExperience) &&
    workExperience.length > 0
  ) {
    html += '<div data-section="experience" style="margin-bottom: 30px;">\n';
    html +=
      '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Work Experience</h2>\n';

    workExperience.forEach(function (job, index) {
      // Spacing between jobs
      if (index > 0) {
        html += '<div style="margin-top: 20px;"></div>\n';
      }

      var jobTitle = escapeHtml(job.jobTitle || "");
      var companyName = escapeHtml(job.companyName || job.company || "");
      var location = escapeHtml(job.location || "");

      var startDate = formatDate(job.startDate);
      var endDate =
        !job.endDate || job.endDate === "9999-01-01"
          ? "Present"
          : formatDate(job.endDate);

      html += '<div data-element="job">\n';
      html +=
        '<h3 data-element="job-title" style="font-size: 16px; font-weight: 500; margin: 0 0 5px 0;">' +
        jobTitle +
        "</h3>\n";
      html +=
        '<p data-element="job-company" style="margin: 0 0 5px 0; font-size: 15px;"><strong class="preserve-bold">' +
        companyName +
        "</strong></p>\n";
      html +=
        '<p data-element="job-period" style="margin: 0 0 10px 0; font-size: 14px; color: #666;">' +
        escapeHtml(startDate) +
        " - " +
        escapeHtml(endDate) +
        " | " +
        location +
        "</p>\n";

      if (job.description) {
        html +=
          '<p data-element="job-description" style="margin: 0 0 10px 0; font-size: 15px;">' +
          escapeHtml(job.description) +
          "</p>\n";
      }

      // Responsibilities
      if (job.responsibilities) {
        html +=
          '<ul data-element="job-responsibilities" style="margin: 0 0 10px 0; padding-left: 20px; font-size: 15px;">\n';
        if (typeof job.responsibilities === "string") {
          var responsibilitiesList = job.responsibilities
            .split(/[\n\r]+|(?<=\.)(?=\s)/)
            .map(function (item) {
              return item.trim();
            })
            .filter(function (item) {
              return item.length > 0;
            });
          responsibilitiesList.forEach(function (resp) {
            html += "<li>" + escapeHtml(resp) + "</li>\n";
          });
        } else if (Array.isArray(job.responsibilities)) {
          job.responsibilities.forEach(function (resp) {
            if (typeof resp === "string") {
              html += "<li>" + escapeHtml(resp) + "</li>\n";
            } else if (resp && typeof resp === "object") {
              html +=
                "<li>" +
                escapeHtml(resp.text || JSON.stringify(resp)) +
                "</li>\n";
            }
          });
        }
        html += "</ul>\n";
      }

      // Achievements
      if (
        job.achievements &&
        Array.isArray(job.achievements) &&
        job.achievements.length > 0
      ) {
        html +=
          '<p data-element="achievements-header" style="margin: 10px 0 5px 0; font-size: 15px;"><strong class="preserve-bold">Key Achievements:</strong></p>\n';
        html +=
          '<ul data-element="job-achievements" style="margin: 0 0 10px 0; padding-left: 20px; font-size: 15px;">\n';
        job.achievements.forEach(function (ach) {
          html += "<li>" + escapeHtml(ach) + "</li>\n";
        });
        html += "</ul>\n";
      }

      html += "</div>\n"; // Close job
    });

    html += "</div>\n";
  }

  // -----------
  // EDUCATION
  // -----------
  if (education && Array.isArray(education) && education.length > 0) {
    html += '<div data-section="education" style="margin-bottom: 30px;">\n';
    html +=
      '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Education</h2>\n';

    education.forEach(function (edu, index) {
      if (index > 0) {
        html += '<div style="margin-top: 15px;"></div>\n';
      }
      var degree = escapeHtml(edu.degree || "");
      var fieldOfStudy = edu.fieldOfStudy
        ? "in " + escapeHtml(edu.fieldOfStudy)
        : "";
      var institution = escapeHtml(edu.institution || "");
      var graduationDate = edu.graduationDate
        ? formatDate(edu.graduationDate)
        : "";
      var gpa = edu.gpa ? escapeHtml(edu.gpa) : "";

      html += '<div data-element="education-item">\n';
      html +=
        '<h3 data-element="education-degree" style="font-size: 16px; font-weight: 500; margin: 0 0 5px 0;">' +
        degree +
        " " +
        fieldOfStudy +
        "</h3>\n";
      html +=
        '<p data-element="education-institution" style="margin: 0 0 5px 0; font-size: 15px;">' +
        institution +
        "</p>\n";
      html +=
        '<p data-element="education-date" style="margin: 0 0 5px 0; font-size: 14px; color: #666;">' +
        graduationDate +
        "</p>\n";

      if (gpa) {
        html +=
          '<p data-element="education-gpa" style="margin: 0; font-size: 14px;"><strong class="preserve-bold">GPA:</strong> ' +
          gpa +
          "</p>\n";
      }
      html += "</div>\n";
    });

    html += "</div>\n";
  }

  // -------------------------
  // ADDITIONAL QUALIFICATIONS
  // -------------------------
  if (
    additionalQualifications &&
    Array.isArray(additionalQualifications) &&
    additionalQualifications.length > 0
  ) {
    // Group by qualificationType
    var qualificationsByType = {};
    additionalQualifications.forEach(function (qual) {
      if (!qualificationsByType[qual.qualificationType]) {
        qualificationsByType[qual.qualificationType] = [];
      }
      qualificationsByType[qual.qualificationType].push(qual.details);
    });

    html += '<div data-section="additional-qualifications">\n';

    // CERTIFICATIONS
    if (qualificationsByType["Certification"]) {
      html +=
        '<div data-section="certifications" style="margin-bottom: 30px;">\n';
      html +=
        '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Certifications</h2>\n';
      html +=
        '<ul data-element="certification-list" style="margin: 0; padding-left: 20px; font-size: 15px;">\n';
      qualificationsByType["Certification"].forEach(function (cert) {
        var name = escapeHtml(cert.name || "Certification");
        var issuer = cert.issuer ? escapeHtml(cert.issuer) : "";
        var dateObtained = cert.dateObtained
          ? formatDate(cert.dateObtained)
          : "";
        var description = cert.description ? escapeHtml(cert.description) : "";

        html +=
          '<li data-element="certification-item"><strong class="preserve-bold">' +
          name +
          "</strong>";
        if (issuer) {
          html += " (" + issuer;
          if (dateObtained) {
            html += ", " + dateObtained;
          }
          html += ")";
        }
        if (description) {
          html += "<br>" + description;
        }
        html += "</li>\n";
      });
      html += "</ul>\n";
      html += "</div>\n";
    }

    // PERSONAL PROJECTS
    if (qualificationsByType["Personal Project"]) {
      html += '<div data-section="projects" style="margin-bottom: 30px;">\n';
      html +=
        '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">Personal Projects</h2>\n';

      qualificationsByType["Personal Project"].forEach(function (
        project,
        index
      ) {
        if (index > 0) {
          html += '<div style="margin-top: 15px;"></div>\n';
        }
        var projectName = escapeHtml(project.projectName || "");
        var description = project.description
          ? escapeHtml(project.description)
          : "";
        var projSkills = "";
        if (Array.isArray(project.skills)) {
          projSkills = project.skills
            .map(function (s) {
              return escapeHtml(s);
            })
            .join(", ");
        } else {
          projSkills = escapeHtml(project.skills || "");
        }
        var projectUrl = project.projectUrl
          ? escapeHtml(project.projectUrl)
          : "";
        var outcome = project.outcome ? escapeHtml(project.outcome) : "";

        html += '<div data-element="project">\n';
        html +=
          '<h3 data-element="project-name" style="font-size: 16px; font-weight: 500; margin: 0 0 5px 0;">' +
          projectName +
          "</h3>\n";

        if (description) {
          html +=
            '<p data-element="project-description" style="margin: 0 0 5px 0; font-size: 15px;">' +
            description +
            "</p>\n";
        }

        // Skills
        if (projSkills) {
          html +=
            '<p data-element="project-skills" style="margin: 0 0 5px 0; font-size: 14px;"><strong class="preserve-bold">Skills Utilized:</strong> ' +
            projSkills +
            "</p>\n";
        }

        // Outcome
        if (outcome) {
          html +=
            '<p data-element="project-outcome" style="margin: 0 0 5px 0; font-size: 14px;"><strong class="preserve-bold">Outcome:</strong> ' +
            outcome +
            "</p>\n";
        }

        // URL
        if (projectUrl) {
          html +=
            '<p data-element="project-url" style="margin: 0; font-size: 14px;"><a href="' +
            projectUrl +
            '" style="color: #333; text-decoration: none; border-bottom: 1px dotted #333;">' +
            projectUrl +
            "</a></p>\n";
        }

        html += "</div>\n"; // close project
      });

      html += "</div>\n";
    }

    // (Other qualification types would follow here, using similar structure.)
    html += "</div>\n"; // close additional-qualifications
  }

  // ----------
  // REFERENCES
  // ----------
  if (references && Array.isArray(references) && references.length > 0) {
    html += '<div data-section="references" style="margin-bottom: 30px;">\n';
    html +=
      '<h2 data-section-header="true" data-element="section-header" style="font-size: 18px; font-weight: 400; letter-spacing: 1px; margin-bottom: 10px; text-transform: uppercase;">References</h2>\n';

    // If references[0].available === false
    if (references[0].available === false) {
      html +=
        '<p data-element="references-available" style="margin: 0; font-size: 15px; font-style: italic;">References available upon request</p>\n';
    } else {
      html +=
        '<ul data-element="references-list" style="list-style: none; padding-left: 0;">\n';
      references.forEach(function (ref, idx) {
        if (idx > 0) {
          html += '<div style="margin-top: 10px;"></div>\n';
        }
        var refName = escapeHtml(ref.name || "");
        var refTitle = ref.title ? ", " + escapeHtml(ref.title) : "";
        var refCompany = ref.company ? ", " + escapeHtml(ref.company) : "";
        var refEmail = ref.email ? escapeHtml(ref.email) : "";
        var refPhone = ref.phone ? escapeHtml(ref.phone) : "";

        html +=
          '<li data-element="reference-item" style="margin-bottom: 10px;">\n';
        html +=
          '<p style="margin: 0 0 5px 0; font-size: 15px;"><strong class="preserve-bold">' +
          refName +
          "</strong>" +
          refTitle +
          refCompany +
          "</p>\n";
        if (refEmail || refPhone) {
          html += '<p style="margin: 0; font-size: 14px;">';
          if (refEmail) {
            html += refEmail;
          }
          if (refEmail && refPhone) {
            html += " | ";
          }
          if (refPhone) {
            html += refPhone;
          }
          html += "</p>\n";
        }
        html += "</li>\n";
      });
      html += "</ul>\n";
    }
    html += "</div>\n";
  }

  // Close outer container
  html += "</div>\n";

  // That’s it! Now the returned HTML is “gold standard.”
  return html;
};

export default minimalistTemplateHtml;
